import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useLayoutEffect, forwardRef } from 'react';
import { Input } from '../Input';
import { cn } from '../utils/tailwind';
import { useLocalizedStrings } from '../LocalizationProvider';
import { getTextSizeClasses } from './utils';
const DEFAULT_MIN_WIDTH = 16;
export const DATE_TIME_INPUT_TYPES = ['date', 'datetime-local', 'time', 'month', 'week'];
export const AutosizedInput = forwardRef(({ value = '', placeholder = '', textElement = 'span', disabled = false, required = false, maxLength, ariaLabel, onKeyDown, onBlur, onChange, error, isNumeric = false, isCurrency = false, formatPattern, mask = "_", currencySymbol = "$", decimalScale = 2, thousandSeparator = true, decimalSeparator = ".", allowNegative = true, valueIsNumericString = false, numberFormatProps, type = 'text', className }, ref) => {
    const L = useLocalizedStrings();
    const [inputWidth, setInputWidth] = useState('auto');
    const [placeholderWidth, setPlaceholderWidth] = useState(DEFAULT_MIN_WIDTH);
    const measureRef = useRef(null);
    useLayoutEffect(() => {
        if (DATE_TIME_INPUT_TYPES.includes(type)) {
            const fixedWidths = {
                'time': '100px',
                'date': '125px',
                'datetime-local': '200px',
                'month': '160px',
                'week': '180px',
            };
            setInputWidth(fixedWidths[type]);
            return;
        }
        if (measureRef.current) {
            let finalWidth;
            if (formatPattern) {
                measureRef.current.textContent = formatPattern;
                measureRef.current.className = cn("absolute opacity-0 pointer-events-none whitespace-pre-wrap", getTextSizeClasses(textElement));
                const patternWidth = measureRef.current.getBoundingClientRect().width;
                finalWidth = Math.ceil(patternWidth) + 16;
            }
            else {
                const valueAsString = value != null ? String(value) : '';
                let textToMeasure;
                let isPlaceholderText = false;
                const isValueEmpty = !valueAsString.trim() || valueAsString === 'NaN';
                if (isValueEmpty) {
                    textToMeasure = placeholder || '';
                    isPlaceholderText = true;
                }
                else {
                    textToMeasure = valueAsString;
                }
                measureRef.current.textContent = textToMeasure;
                if (isPlaceholderText) {
                    measureRef.current.className = cn("absolute opacity-0 pointer-events-none whitespace-pre-wrap", getTextSizeClasses(textElement), "text-gray-500");
                }
                else {
                    measureRef.current.className = cn("absolute opacity-0 pointer-events-none whitespace-pre-wrap", getTextSizeClasses(textElement));
                }
                const measuredWidth = measureRef.current.getBoundingClientRect().width;
                const minWidth = isPlaceholderText ? Math.max(measuredWidth, placeholderWidth) : measuredWidth;
                const buffer = isCurrency ? 50 : 24;
                finalWidth = Math.ceil(minWidth) + buffer;
            }
            setInputWidth(`${finalWidth}px`);
        }
    }, [value, placeholder, placeholderWidth, formatPattern]);
    useLayoutEffect(() => {
        if (measureRef.current && placeholder) {
            measureRef.current.textContent = placeholder;
            measureRef.current.className = cn("absolute opacity-0 pointer-events-none whitespace-pre-wrap", getTextSizeClasses(textElement), "text-gray-500");
            const placeholderWidth = measureRef.current.getBoundingClientRect().width;
            setPlaceholderWidth(Math.max(placeholderWidth + 12, DEFAULT_MIN_WIDTH));
        }
    }, [placeholder]);
    return (_jsxs(_Fragment, { children: [_jsx("span", { ref: measureRef, className: cn("absolute opacity-0 pointer-events-none whitespace-pre-wrap", getTextSizeClasses(textElement)), "aria-hidden": "true" }), _jsx(Input, { ref: ref, label: ariaLabel || L.component_library.inline_editable_text.edit_field, showLabel: false, value: value, onChange: onChange, onKeyDown: onKeyDown, onBlur: onBlur, disabled: disabled, error: error, required: required, maxLength: maxLength, placeholder: placeholder, textVariant: textElement, inline: true, isNumeric: isNumeric, isCurrency: isCurrency, formatPattern: formatPattern, mask: mask, currencySymbol: currencySymbol, decimalScale: decimalScale, thousandSeparator: thousandSeparator, decimalSeparator: decimalSeparator, allowNegative: allowNegative, valueIsNumericString: valueIsNumericString, numberFormatProps: numberFormatProps, className: cn(getTextSizeClasses(textElement), className), style: {
                    width: inputWidth,
                    minWidth: inputWidth,
                    transitionProperty: 'width, min-width',
                }, type: type })] }));
});
